import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher'
import { PageHero } from '@/components/ui/PageHero'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/contexts/ToastContext'
import { ApiError } from '@/services/api/client'
import { settingsApi } from '@/services/api/settings'
import { cn } from '@/lib/cn'

type NotifKey = 'orders' | 'price' | 'funding' | 'news'

/**
 * Account & terminal preferences — theme, language, notifications, session.
 * Profile fields → `PATCH /client/settings` (API_DOCUMENTATION §5.5).
 */
export function SettingsPage({ className }: { className?: string }) {
  const { t } = useTranslation('common')
  const { logout, isAuthenticated, client, tradeMode, refreshMe } = useAuth()
  const { pushToast } = useToast()
  const [logoutOpen, setLogoutOpen] = useState(false)
  const [notifs, setNotifs] = useState<Record<NotifKey, boolean>>({
    orders: true,
    price: true,
    funding: true,
    news: false,
  })
  const [confirmOrders, setConfirmOrders] = useState(true)
  const [compact, setCompact] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [savingProfile, setSavingProfile] = useState(false)

  useEffect(() => {
    if (!client) return
    setName(client.name)
    setEmail(client.email)
    setPhone(client.phone ?? '')
  }, [client])

  const onSaveProfile = async (e: FormEvent) => {
    e.preventDefault()
    setSavingProfile(true)
    try {
      const body: {
        name?: string
        email?: string
        phone?: string | null
        password?: string
        password_confirmation?: string
      } = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
      }
      if (password) {
        body.password = password
        body.password_confirmation = passwordConfirmation
      }
      await settingsApi.update(body)
      await refreshMe()
      setPassword('')
      setPasswordConfirmation('')
      pushToast({ tone: 'success', title: t('auth.profileSaved') })
    } catch (err) {
      pushToast({
        tone: 'danger',
        title: t('auth.loginFailed'),
        message: err instanceof ApiError ? err.message : undefined,
      })
    } finally {
      setSavingProfile(false)
    }
  }

  const toggleNotif = (key: NotifKey) => {
    setNotifs((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      pushToast({
        tone: 'success',
        title: t('settings.saved'),
      })
      return next
    })
  }

  return (
    <div
      className={cn(
        'terminal-scroll h-full min-h-0 overflow-y-auto',
        className,
      )}
    >
      <PageHero
        tone="info"
        glow="start"
        eyebrow={t('settings.eyebrow')}
        title={t('nav.settings')}
        subtitle={t('settings.subtitle')}
      />

      <div className="mx-auto flex max-w-2xl flex-col gap-3 p-4 sm:gap-4 sm:p-6">
        <SettingsCard title={t('settings.appearance')}>
          <SettingsRow
            label={t('settings.theme')}
            hint={t('settings.themeHint')}
          >
            <ThemeSwitcher />
          </SettingsRow>
          <SettingsRow
            label={t('language')}
            hint={t('settings.languageHint')}
          >
            <LanguageSwitcher />
          </SettingsRow>
          <ToggleRow
            label={t('settings.compact')}
            hint={t('settings.compactHint')}
            checked={compact}
            onChange={() => {
              setCompact((v) => !v)
              pushToast({ tone: 'success', title: t('settings.saved') })
            }}
          />
        </SettingsCard>

        <SettingsCard title={t('settings.trading')}>
          <ToggleRow
            label={t('settings.confirmOrders')}
            hint={t('settings.confirmOrdersHint')}
            checked={confirmOrders}
            onChange={() => {
              setConfirmOrders((v) => !v)
              pushToast({ tone: 'success', title: t('settings.saved') })
            }}
          />
        </SettingsCard>

        <SettingsCard title={t('settings.notifications')}>
          {(
            [
              ['orders', 'settings.notifOrders', 'settings.notifOrdersHint'],
              ['price', 'settings.notifPrice', 'settings.notifPriceHint'],
              ['funding', 'settings.notifFunding', 'settings.notifFundingHint'],
              ['news', 'settings.notifNews', 'settings.notifNewsHint'],
            ] as const
          ).map(([key, label, hint]) => (
            <ToggleRow
              key={key}
              label={t(label)}
              hint={t(hint)}
              checked={notifs[key]}
              onChange={() => toggleNotif(key)}
            />
          ))}
        </SettingsCard>

        {client ? (
          <SettingsCard title={t('auth.profileTitle')}>
            <form className="space-y-3 p-4" onSubmit={(e) => void onSaveProfile(e)}>
              <p className="text-[11px] text-terminal-muted">
                {t('auth.accountId')}:{' '}
                <span className="font-mono font-semibold text-terminal-fg">
                  {client.account_id}
                </span>
                {tradeMode ? (
                  <>
                    {' · '}
                    {tradeMode === 'full'
                      ? t('auth.tradeModeFull')
                      : t('auth.tradeModeRead')}
                  </>
                ) : null}
              </p>
              <label className="flex flex-col gap-1 text-xs text-terminal-muted">
                Name
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="h-9 rounded border border-terminal-border/50 bg-terminal-bg px-2.5 text-sm text-terminal-fg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-terminal-muted">
                {t('auth.email')}
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-9 rounded border border-terminal-border/50 bg-terminal-bg px-2.5 text-sm text-terminal-fg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-terminal-muted">
                {t('auth.phone')}
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-9 rounded border border-terminal-border/50 bg-terminal-bg px-2.5 text-sm text-terminal-fg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent"
                />
              </label>
              <PasswordInput
                id="settings-password"
                label={t('auth.changePassword')}
                value={password}
                onChange={setPassword}
                showStrength={Boolean(password)}
              />
              {password ? (
                <PasswordInput
                  id="settings-password-confirm"
                  label={t('auth.confirmPassword')}
                  value={passwordConfirmation}
                  onChange={setPasswordConfirmation}
                  showStrength={false}
                  required
                />
              ) : null}
              <button
                type="submit"
                disabled={savingProfile}
                className="h-9 rounded bg-accent px-4 text-xs font-semibold text-white hover:bg-accent-muted disabled:opacity-50"
              >
                {savingProfile ? t('actions.working') : t('auth.saveProfile')}
              </button>
            </form>
          </SettingsCard>
        ) : null}

        <SettingsCard title={t('settings.session')}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4">
            <div>
              <p className="text-xs font-medium text-terminal-fg">
                {t('settings.sessionLabel')}
              </p>
              <p className="mt-0.5 text-[11px] text-terminal-muted">
                {isAuthenticated
                  ? t('settings.sessionActive')
                  : t('settings.sessionGuest')}
              </p>
            </div>
            {isAuthenticated ? (
              <button
                type="button"
                onClick={() => setLogoutOpen(true)}
                className="h-9 rounded-lg border border-danger/40 px-4 text-xs font-semibold text-danger hover:bg-danger/10"
              >
                {t('actions.logout')}
              </button>
            ) : null}
          </div>
        </SettingsCard>

        <p className="px-1 text-center text-[10px] text-terminal-muted">
          Velox Terminal · v0.1.0
        </p>
      </div>

      <ConfirmModal
        open={logoutOpen}
        title={t('logout_confirm_title')}
        message={t('logout_confirm_message')}
        tone="danger"
        confirmLabel={t('actions.logout')}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={() => {
          void logout().then(() => {
            setLogoutOpen(false)
            window.location.assign('/login')
          })
        }}
      />
    </div>
  )
}

function SettingsCard({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-terminal-border/40 bg-terminal-surface">
      <header className="border-b border-terminal-border/40 px-4 py-2.5">
        <h2 className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
          {title}
        </h2>
      </header>
      <div className="divide-y divide-terminal-border/40">{children}</div>
    </section>
  )
}

function SettingsRow({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="text-xs font-medium text-terminal-fg">{label}</p>
        {hint ? (
          <p className="mt-0.5 text-[11px] text-terminal-muted">{hint}</p>
        ) : null}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  )
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string
  hint?: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-start hover:bg-terminal-elevated/40"
    >
      <div className="min-w-0">
        <p className="text-xs font-medium text-terminal-fg">{label}</p>
        {hint ? (
          <p className="mt-0.5 text-[11px] text-terminal-muted">{hint}</p>
        ) : null}
      </div>
      <span
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full transition-colors',
          checked ? 'bg-accent' : 'bg-terminal-border',
        )}
        aria-hidden
      >
        <span
          className={cn(
            'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
            checked ? 'start-4' : 'start-0.5',
          )}
        />
      </span>
    </button>
  )
}
